import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { SITE_CONFIG } from '@vastra/constants';
import { t } from '../lib/i18n';
import LocaleSwitcher from './LocaleSwitcher';

interface NavbarProps {
  cartCount: number;
  wishlistCount: number;
  isAuthenticated: boolean;
  userName?: string;
  /** e.g. "Customer", "Admin" - shown in top right when logged in. */
  userRole?: string;
  onLogout: () => void;
  /** Categories derived from product data (exclude "All"). Used for Collections dropdown. */
  categories?: string[];
  /** Product styles (tags) derived from product data. Used for Styles dropdown. Only shown if tags exist. */
  tags?: string[];
  /** Whether to show the language switcher. */
  multiLanguageEnabled?: boolean;
}

/** First character of first name, uppercase, or "U" as fallback. */
function userInitial(name?: string): string {
  if (!name || !name.trim()) return 'U';
  const first = name.trim().split(/\s+/)[0];
  return (first?.charAt(0)?.toUpperCase()) || 'U';
}

const JOURNAL_PATH = '/blog';

const Navbar: React.FC<NavbarProps> = ({ cartCount, wishlistCount, isAuthenticated, userName, userRole, onLogout, categories = [], tags = [], multiLanguageEnabled = false }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const collectionsRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
    setCollectionsOpen(false);
    setTagsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  useEffect(() => {
    if (!collectionsOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (collectionsRef.current && !collectionsRef.current.contains(e.target as Node)) setCollectionsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [collectionsOpen]);

  useEffect(() => {
    if (!tagsOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (tagsRef.current && !tagsRef.current.contains(e.target as Node)) setTagsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [tagsOpen]);

  const linkClasses = (isActive: boolean) =>
    `text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 py-1 border-b ${
      isActive ? 'text-[#14212b] border-[#1f6f66]' : 'text-[#5a6570] border-transparent hover:text-[#14212b] hover:border-[#1f6f66]/50'
    }`;

  const collectionsActive = location.pathname === '/products' || location.pathname.startsWith('/products/');

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-xl border-b border-[#14212b]/8 shadow-[0_8px_28px_rgba(20,33,43,0.07)]'
            : 'bg-gradient-to-b from-white via-white to-[#f7fafb]/90 backdrop-blur-md border-b border-transparent'
        }`}
        role="banner"
      >
        <nav className="relative max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-10 h-20" aria-label="Main navigation">
          {/* Left: Hamburger (mobile) + Logo + Brand name */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              className="lg:hidden p-2 -ml-2 text-[#2a3a48] hover:text-[#14212b] transition-colors"
              onClick={() => setMobileOpen(true)}
              aria-label={t('i18n.customer.navigation.en.aria.openMenu', 'Open navigation menu')}
              aria-expanded={mobileOpen}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <Link to="/" className="flex items-center gap-2 sm:gap-3" aria-label={`${SITE_CONFIG.siteName} home`}>
              <img src="/logo.jpg" alt="Style with Vikashini" className="h-12 sm:h-14 lg:h-16 object-contain notranslate" />
              <div className="flex flex-col gap-1.5 py-0.5">
                <span className="notranslate text-sm sm:text-base lg:text-lg font-medium serif tracking-wide text-[#14212b] leading-tight">{SITE_CONFIG.siteName}</span>
                <span className="notranslate hidden sm:block text-[7px] lg:text-[8px] font-semibold uppercase tracking-[0.28em] text-[#1f6f66] leading-none">{SITE_CONFIG.tagline}</span>
              </div>
            </Link>
          </div>

          {/* Center: Desktop nav — Home, Collections (dropdown), Journal */}
          <div className="hidden lg:flex absolute left-1/2 top-0 h-full -translate-x-1/2 items-center gap-8 pointer-events-none">
            <div className="flex items-center gap-8 pointer-events-auto">
              <NavLink to="/" end className={({ isActive }) => linkClasses(isActive)}>
                {t('i18n.customer.navigation.en.desktop.home', 'Home')}
              </NavLink>

              <div className="relative" ref={collectionsRef}>
                <button
                  type="button"
                  className={`${linkClasses(collectionsActive)} flex items-center gap-1 bg-transparent border-0 cursor-pointer font-inherit`}
                  aria-expanded={collectionsOpen}
                  aria-haspopup="true"
                  aria-controls="collections-menu"
                  onClick={() => setCollectionsOpen((o) => !o)}
                >
                  {t('i18n.customer.navigation.en.desktop.collections', 'Collections')}
                  <svg className={`w-3 h-3 transition-transform ${collectionsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {collectionsOpen && (
                  <div
                    id="collections-menu"
                    role="menu"
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-2 min-w-[220px] max-h-[70vh] overflow-y-auto bg-[#ffffff] border border-[#14212b]/10 shadow-[0_12px_40px_rgba(20,33,43,0.12)] py-2 z-50 animate-fade-in"
                  >
                    <Link
                      role="menuitem"
                      to="/products"
                      className="block px-4 py-2.5 text-xs font-medium text-[#14212b] hover:bg-[#f4f7f9]"
                      onClick={() => setCollectionsOpen(false)}
                    >
                      {t('i18n.customer.navigation.en.desktop.allProducts', 'All products')}
                    </Link>
                    <div className="h-px bg-[#14212b]/8 my-1" aria-hidden />
                    {categories.length === 0 ? (
                      <p className="px-4 py-2 text-xs text-[#8a847a]">{t('i18n.customer.navigation.en.desktop.noCategories', 'No categories yet')}</p>
                    ) : (
                      categories.map((cat) => (
                        <Link
                          key={cat}
                          role="menuitem"
                          to={`/products?category=${encodeURIComponent(cat)}`}
                          className="block px-4 py-2.5 text-xs text-[#5a6570] hover:bg-[#f4f7f9] hover:text-[#14212b]"
                          onClick={() => setCollectionsOpen(false)}
                        >
                          {cat}
                        </Link>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div className="relative" ref={tagsRef}>
                {tags.length > 0 && (
                  <>
                    <button
                      type="button"
                      className={`${linkClasses(false)} flex items-center gap-1 bg-transparent border-0 cursor-pointer font-inherit`}
                      aria-expanded={tagsOpen}
                      aria-haspopup="true"
                      aria-controls="styles-menu"
                      onClick={() => setTagsOpen((o) => !o)}
                    >
                      {t('i18n.customer.navigation.en.desktop.styles', 'Curations')}
                      <svg className={`w-3 h-3 transition-transform ${tagsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {tagsOpen && (
                      <div
                        id="styles-menu"
                        role="menu"
                        className="absolute left-1/2 -translate-x-1/2 top-full mt-2 min-w-[220px] max-h-[70vh] overflow-y-auto bg-[#ffffff] border border-[#14212b]/10 shadow-[0_12px_40px_rgba(20,33,43,0.12)] py-2 z-50 animate-fade-in"
                      >
                        {tags.map((tag) => (
                          <Link
                            key={tag}
                            role="menuitem"
                            to={`/products?tag=${encodeURIComponent(tag)}`}
                            className="block px-4 py-2.5 text-xs text-[#5a6570] hover:bg-[#f4f7f9] hover:text-[#14212b]"
                            onClick={() => setTagsOpen(false)}
                          >
                            {tag}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <NavLink to={JOURNAL_PATH} className={({ isActive }) => linkClasses(isActive)}>
                {t('i18n.customer.navigation.en.journal', 'Journal')}
              </NavLink>
            </div>
          </div>

          {/* Right: Action icons */}
          <div className="flex items-center justify-end gap-2 sm:gap-3 lg:gap-6 shrink-0">
            <LocaleSwitcher className="hidden sm:flex" multiLanguageEnabled={multiLanguageEnabled} />
            {/* Wishlist */}
            <Link
              to="/wishlist"
              className="relative p-1 text-[#5a6570] hover:text-[#14212b] transition-colors"
              aria-label={
                wishlistCount > 0
                  ? t('i18n.customer.navigation.en.aria.wishlistWithCount', 'Wishlist, {count} items').replace('{count}', String(wishlistCount))
                  : t('i18n.customer.navigation.en.aria.wishlist', 'Wishlist')
              }
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1.5 bg-[#1f6f66] text-[#ffffff] text-[8px] font-bold w-4 h-4 flex items-center justify-center rounded-full" aria-hidden="true">
                  {wishlistCount}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              className="relative p-1 text-[#5a6570] hover:text-[#14212b] transition-colors"
              aria-label={
                cartCount > 0
                  ? t('i18n.customer.navigation.en.aria.shoppingBagWithCount', 'Shopping bag, {count} items').replace('{count}', String(cartCount))
                  : t('i18n.customer.navigation.en.aria.shoppingBag', 'Shopping bag')
              }
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1.5 bg-[#1f6f66] text-[#ffffff] text-[8px] font-bold w-4 h-4 flex items-center justify-center rounded-full" aria-hidden="true">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* User menu: when logged in show name, role and avatar with initial */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-1 sm:gap-2.5 text-[#5a6570] hover:text-[#14212b] transition-colors rounded-full focus:outline-none focus:ring-2 focus:ring-[#1f6f66] focus:ring-offset-2"
                aria-label={
                  isAuthenticated
                    ? t('i18n.customer.navigation.en.aria.accountMenu', 'Account menu for {name}').replace('{name}', userName ?? 'User')
                    : t('i18n.customer.navigation.en.aria.account', 'Account')
                }
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
              >
                {isAuthenticated ? (
                  <>
                    <span className="hidden sm:inline text-right">
                      <span className="block text-xs font-medium text-[#14212b] truncate max-w-[100px]">{userName}</span>
                      {userRole && <span className="block text-[10px] text-[#5a6570] uppercase tracking-wide">{userRole}</span>}
                    </span>
                    <span className="w-8 h-8 rounded-full bg-[#14212b] text-[#ffffff] flex items-center justify-center text-sm font-bold shrink-0" title={userName}>{userInitial(userName)}</span>
                  </>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                )}
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-3 w-56 bg-[#ffffff] shadow-[0_12px_40px_rgba(20,33,43,0.12)] border border-[#14212b]/10 animate-fade-in" role="menu">
                  {isAuthenticated ? (
                    <>
                      <div className="px-6 py-4 border-b border-[#14212b]/8">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-[#8a847a]">{t('i18n.customer.navigation.en.userMenu.signedInAs', 'Signed in as')}</p>
                        <p className="text-sm font-semibold text-[#14212b] mt-1 truncate">{userName}</p>
                      </div>
                      <Link to="/profile" className="block px-6 py-3 text-xs font-medium text-[#2a3a48] hover:bg-[#f4f7f9] transition-colors" role="menuitem">{t('i18n.customer.navigation.en.userMenu.myProfile', 'My Profile')}</Link>
                      <Link to="/my-orders" className="block px-6 py-3 text-xs font-medium text-[#2a3a48] hover:bg-[#f4f7f9] transition-colors" role="menuitem">{t('i18n.customer.navigation.en.userMenu.myOrders', 'My Orders')}</Link>
                      <Link to="/order-status" className="block px-6 py-3 text-xs font-medium text-[#2a3a48] hover:bg-[#f4f7f9] transition-colors" role="menuitem">{t('i18n.customer.navigation.en.userMenu.trackOrder', 'Track order')}</Link>
                      <Link to="/wishlist" className="block px-6 py-3 text-xs font-medium text-[#2a3a48] hover:bg-[#f4f7f9] transition-colors" role="menuitem">{t('i18n.customer.navigation.en.userMenu.wishlist', 'Wishlist')}</Link>
                      <button onClick={onLogout} className="block w-full text-left px-6 py-3 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors border-t border-[#14212b]/8" role="menuitem">{t('i18n.customer.navigation.en.userMenu.signOut', 'Sign Out')}</button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" className="block px-6 py-4 text-xs font-semibold uppercase tracking-widest text-[#14212b] hover:bg-[#f4f7f9] transition-colors" role="menuitem">{t('i18n.customer.navigation.en.userMenu.signIn', 'Sign In')}</Link>
                      <Link to="/order-status" className="block px-6 py-3 text-xs font-medium text-[#5a6570] hover:bg-[#f4f7f9] transition-colors border-t border-[#14212b]/8" role="menuitem">{t('i18n.customer.navigation.en.userMenu.trackOrder', 'Track order')}</Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </nav>

        {/* Decorative bottom line */}
        <div className="h-px bg-[#14212b]/10" aria-hidden="true" />
      </header>

      {/* Spacer for fixed header */}
      <div className="h-20" aria-hidden="true" />

      {/* Mobile slide-out menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden" role="dialog" aria-modal="true" aria-label={t('i18n.customer.navigation.en.aria.mobileNav', 'Mobile navigation')}>
          <div className="absolute inset-0 bg-[#0a1218]/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          <div ref={menuRef} className="absolute inset-y-0 left-0 w-80 max-w-[85vw] bg-[#ffffff] shadow-2xl animate-slide-in-left flex flex-col">
            <div className="flex items-center justify-between px-8 h-20 border-b border-[#14212b]/8">
              <div className="flex items-center gap-2 min-w-0">
                <img src="/logo.jpg" alt="Style with Vikashini" className="h-12 w-12 object-contain shrink-0" />
                <span className="notranslate text-sm font-medium serif text-[#14212b] py-1 truncate">{SITE_CONFIG.siteName}</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-2 text-[#8a847a] hover:text-[#14212b] transition-colors" aria-label={t('i18n.customer.navigation.en.aria.closeMenu', 'Close navigation menu')}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-6" aria-label={t('i18n.customer.navigation.en.aria.mobileNav', 'Mobile navigation')}>
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `block px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                    isActive ? 'text-[#14212b] bg-[#f4f7f9]' : 'text-[#5a6570] hover:text-[#14212b] hover:bg-[#f4f7f9]'
                  }`
                }
              >
                {t('i18n.customer.navigation.en.desktop.home', 'Home')}
              </NavLink>
              <NavLink
                to="/products"
                className={({ isActive }) =>
                  `block px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                    isActive ? 'text-[#14212b] bg-[#f4f7f9]' : 'text-[#5a6570] hover:text-[#14212b] hover:bg-[#f4f7f9]'
                  }`
                }
              >
                {t('i18n.customer.navigation.en.desktop.collections', 'Collections')}
              </NavLink>
              <NavLink
                to={JOURNAL_PATH}
                className={({ isActive }) =>
                  `block px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                    isActive ? 'text-[#14212b] bg-[#f4f7f9]' : 'text-[#5a6570] hover:text-[#14212b] hover:bg-[#f4f7f9]'
                  }`
                }
              >
                {t('i18n.customer.navigation.en.journal', 'Journal')}
              </NavLink>
              <div className="h-px bg-[#14212b]/8 mx-8 my-4" aria-hidden="true" />
              <p className="px-8 pb-2 text-[9px] font-semibold uppercase tracking-widest text-[#8a847a]">{t('i18n.customer.navigation.en.mobile.shopByCategory', 'Shop by category')}</p>
              {categories.map((cat) => (
                <Link
                  key={cat}
                  to={`/products?category=${encodeURIComponent(cat)}`}
                  className="block px-8 py-2.5 text-xs text-[#5a6570] hover:text-[#14212b] hover:bg-[#f4f7f9] transition-colors"
                >
                  {cat}
                </Link>
              ))}
            </nav>
            <div className="border-t border-[#14212b]/8 p-8 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-[#8a847a]">
                  {t('i18n.customer.navigation.en.locale.label', 'Language')}
                </span>
                <LocaleSwitcher multiLanguageEnabled={multiLanguageEnabled} />
              </div>
              <Link
                to="/order-status"
                className="block text-center text-xs font-medium text-[#5a6570] hover:text-[#14212b] py-2 border border-[#14212b]/15 rounded-sm"
              >
                {t('i18n.customer.navigation.en.userMenu.trackOrder', 'Track order')}
              </Link>
              {isAuthenticated ? (
                <div className="space-y-3">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-[#8a847a]">
                    {t('i18n.customer.navigation.en.mobile.hello', 'Hello, {name}').replace('{name}', userName ?? '')}
                  </p>
                  <Link to="/profile" className="block text-xs text-[#2a3a48] hover:text-[#14212b]">{t('i18n.customer.navigation.en.mobile.profile', 'Profile')}</Link>
                  <button onClick={onLogout} className="text-xs text-red-600 hover:text-red-700">{t('i18n.customer.navigation.en.userMenu.signOut', 'Sign Out')}</button>
                </div>
              ) : (
                <Link to="/login" className="block text-center btn-primary py-3 text-[10px] font-bold uppercase tracking-[0.2em] rounded-sm">
                  {t('i18n.customer.navigation.en.userMenu.signIn', 'Sign In')}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
